import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  Share as ShareIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { gamoraApi } from '../services/api';

interface ShareDialogProps {
  open: boolean;
  onClose: () => void;
  sessionId: string;
  sessionName?: string;
}

const ShareDialog: React.FC<ShareDialogProps> = ({
  open,
  onClose,
  sessionId,
  sessionName,
}) => {
  const [shareUrl, setShareUrl] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleGenerateLink = async () => {
    if (!sessionId) {
      setError('Session ID is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await gamoraApi.generateShareLink(sessionId);
      setShareUrl(result.share_url);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to generate share link');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!shareUrl) return;

    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setError('Failed to copy link to clipboard');
    }
  };

  const handleRevokeLink = async () => {
    if (!sessionId) return;

    setLoading(true);
    setError(null);

    try {
      await gamoraApi.revokeShareLink(sessionId);
      setShareUrl('');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to revoke share link');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShareUrl('');
    setError(null);
    setCopied(false);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShareIcon sx={{ color: '#1976d2' }} />
            <Typography variant="h6">Share Chat Session</Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {sessionName && (
            <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
              Session: <strong>{sessionName}</strong>
            </Typography>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!shareUrl ? (
            <Box>
              <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                Generate a shareable link that allows anyone with the link to view this chat session.
              </Typography>
              <Button
                variant="contained"
                onClick={handleGenerateLink}
                disabled={loading}
                startIcon={<LinkIcon />}
                fullWidth
              >
                {loading ? 'Generating...' : 'Generate Share Link'}
              </Button>
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                Shareable Link:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  value={shareUrl}
                  InputProps={{
                    readOnly: true,
                  }}
                  size="small"
                />
                <Button
                  variant="outlined"
                  onClick={handleCopyLink}
                  startIcon={<ContentCopyIcon />}
                  sx={{ minWidth: 100 }}
                >
                  Copy
                </Button>
              </Box>
              <Button
                variant="outlined"
                color="error"
                onClick={handleRevokeLink}
                disabled={loading}
                fullWidth
              >
                {loading ? 'Revoking...' : 'Revoke Link'}
              </Button>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setCopied(false)}>
          Link copied to clipboard!
        </Alert>
      </Snackbar>
    </>
  );
};

export default ShareDialog;

